//@flow
import React, {useState} from 'react';
import {Chip, Grid, Typography, makeStyles} from '@material-ui/core';

import type {QuickReply as QuickReplyType} from '../../../../entities';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {DeleteOutline, EditOutlined} from '@material-ui/icons';
import {colors} from '../../../../assets/styles/colors';
import {getImageUrl} from '../../../../utils/image_provider';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';
import FileTypeIcons from '../../../../elements/FileTypeIcons';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    borderRadius: '.5rem',
    background: colors.grey100,
    border: `1px solid ${colors.grey200}`,
    transition: 'background .3s ease-in-out',
    maxHeight: '150px',
    overflow: 'auto',

    '&[data-highlight="true"]': {
      background: colors.lightPurple,
    },

    [theme.breakpoints.up('sm')]: {
      overflow: 'hidden',
    },
  },
  idLabel: {
    fontSize: '14px',
    color: colors.grey800,
  },
  textCaption: {
    fontSize: '11px',
    color: colors.grey700,

    '& span': {
      fontWeight: 600,
    },
  },
  responseMessage: {
    fontSize: '14px',
    color: colors.grey800,
    whiteSpace: 'pre-wrap',
  },
  itemActions: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  item: {
    borderTop: `1px solid ${colors.grey300}`,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  switchAction: {
    padding: '.25rem .75rem',
  },
  iconButtonLabel: {
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    width: '100%',
    justifyContent: 'flex-start',
  },
  edit: {
    color: colors.grey700,
    borderRadius: 0,
    height: '100%',
  },
  remove: {
    color: colors.error,
    borderRadius: 0,
    height: '100%',
  },
  switchLabel: {
    fontSize: '14px',
  },
  row: {
    padding: '.5rem .75rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    borderLeft: `1px solid ${colors.grey200}`,
    borderRight: `1px solid ${colors.grey200}`,
    height: '100%',
    maxHeight: 'inherit',
    overflow: 'auto',

    '&:first-child': {
      borderLeft: 'none',
    },

    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },

    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },

    [theme.breakpoints.up('sm')]: {
      padding: '.75rem 1rem',
    },
  },
  labelMobile: {
    fontSize: '14px',
    color: colors.primary,
    fontWeight: 600,

    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  image: {
    minWidth: '3rem',
    minHeight: '3rem',
    width: '3rem',
    height: '3rem',
    borderRadius: '.5rem',
    overflow: 'hidden',
    margin: '.25rem 0',

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  fileIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25rem',
  },
}));

type Props = {
  reply: QuickReplyType,
  onDeleted: () => void,
  onEdited: () => void,
};

export default function QuickReply({reply, onDeleted, onEdited}: Props) {
  const classes = useStyles();

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const closeConfirmModal = () => setConfirmModalOpen(false);
  const openConfirmModal = () => setConfirmModalOpen(true);

  return (
    <>
      <Grid container className={classes.root}>
        <Grid item xs={12} sm={2} className={classes.row}>
          <Typography className={classes.labelMobile}>ID/Nombre</Typography>
          <Typography className={classes.idLabel}>
            {reply.displayName}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={8} className={classes.row}>
          <Typography className={classes.labelMobile}>Mensaje:</Typography>
          <Typography className={classes.responseMessage}>
            {reply.message}
          </Typography>
          {reply.fileId &&
            (reply.fileType === 'IMAGE' ? (
              <picture className={classes.image}>
                <img src={getImageUrl(reply.fileId)} alt="" />
              </picture>
            ) : (
              reply.fileName && (
                <Chip
                  label={reply.fileName}
                  icon={
                    <div className={classes.fileIcon}>
                      <FileTypeIcons type={reply.fileType} />{' '}
                    </div>
                  }
                />
              )
            ))}
        </Grid>
        <Grid item xs={12} sm={2}>
          <div className={classes.itemActions}>
            <div className={classes.item}>
              <CustomButton
                variant="text"
                className={classes.edit}
                fullWidth
                onClick={onEdited}
              >
                <div className={classes.iconButtonLabel}>
                  <EditOutlined />
                  <span>Editar</span>
                </div>
              </CustomButton>
            </div>
            <div className={classes.item}>
              <CustomButton
                variant="text"
                className={classes.remove}
                fullWidth
                onClick={openConfirmModal}
              >
                <div className={classes.iconButtonLabel}>
                  <DeleteOutline />
                  <span>Eliminar</span>
                </div>
              </CustomButton>
            </div>
          </div>
        </Grid>
      </Grid>
      <ConfirmModal
        open={confirmModalOpen}
        onCancel={closeConfirmModal}
        onConfirm={() => {
          onDeleted();
          closeConfirmModal();
        }}
        message="Esta acción no se puede deshacer."
        onClose={closeConfirmModal}
      />
    </>
  );
}
